import React, { useContext, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useAppContext } from 'src/components/app-context';
import {
  useCompareProductsSelection,
  SelectProductToCompareById,
  DeselectProductToCompareById,
} from '../../../../hooks/compare-products-selection';
import { AppState } from '../../../../reducers';
import { ViewportContext } from '../../../../components/viewport';
import CompareProductsBasket from '../../../../components/compare-products-basket';
import AdContainer from '../../../../components/ads';
import {
  ProductsSlice,
  ExpertCategoryContent,
  ProductCollection,
} from '../../../../types';
import { LayoutMode } from '../../../../components/layout-buttons/types';
import { TableLayout } from './layout-table';
import { CompactListLayout } from './layout-compact-list';
import { GridLayout } from './layout-grid';
import { ListLayout } from './layout-list';
import ProductOfferContainer from '../productOffersContainer';
import { setProductCollectionLayout as setProductCollectionLayoutAction } from '../../actions';
import { selectProductLayout, selectSponsoredProducts } from '../../selectors';
import { getSortKeyForProperty } from '../../utils/properties';
import { getPropertyColumnName } from '../../utils/property-columns';
import { trackProductCollectionProductClick } from '../../tracking';
import { ProductComparisonProps } from './types';

import * as S from './styles';
import { trackProductCollectionLayoutChangeClick } from './tracking';
import { SponsoredProduct } from '@prisjakt/web-app-config/markets/types';
import { useSponsoredProductsExperiment } from 'src/hooks/experiments';
import RouteContext from 'src/routes/context';

const getLayout = (preferredViewMode?: string | null) => {
  if (!preferredViewMode) {
    return LayoutMode.GRID;
  }
  // If the preferredViewMode is not set to grid we default to list
  // preferredViewMode can be more things than just "gird" and "list"
  return preferredViewMode === LayoutMode.GRID
    ? LayoutMode.GRID
    : LayoutMode.TABLE;
};

type Props = {
  customLayout: LayoutMode | undefined;
  sponsoredProducts: SponsoredProduct[];
  expert: ExpertCategoryContent | undefined | null;
  setProductCollectionLayout: (mode: LayoutMode) => void;
  productCollection: Pick<
    ProductCollection,
    'categories' | 'categoryPath' | 'slug'
  >;
} & Pick<
  ProductsSlice,
  | 'id'
  | 'pageInfo'
  | 'products'
  | 'productsCount'
  | 'storeCount'
  | 'legalText'
  | 'availableSorts'
  | 'currentSort'
  | 'maxHitsAmount'
  | 'sortDirection'
  | 'propertyColumns'
  | 'preferredViewMode'
>;

const Products = ({
  products,
  sponsoredProducts,
  currentSort,
  sortDirection,
  propertyColumns,
  availableSorts,
  id,
  customLayout,
  setProductCollectionLayout,
  expert,
  productCollection,
  preferredViewMode,
  ...productContainerProps
}: Props) => {
  const { isMobile, isTablet } = useContext(ViewportContext);
  const { linkTo, t } = useAppContext();
  const dispatch = useDispatch();
  const { getState } = useContext(RouteContext);
  const { filters, offset, sort, direction } = getState();
  const { slug: categorySlug } = productCollection;
  const isRootPage = !filters?.length && !offset && !sort && !direction;
  const showSponsoredProducts = useSponsoredProductsExperiment() && isRootPage;
  const filteredSponsoredProducts = useMemo(
    () =>
      showSponsoredProducts
        ? sponsoredProducts.filter(p => p.categorySlug === categorySlug)
        : undefined,
    [sponsoredProducts, categorySlug, showSponsoredProducts]
  );

  useEffect(() => {
    if (customLayout) {
      dispatch({
        type: 'ADS_VIEW_UPDATE',
        view: `productCollection:layout:${customLayout}`,
      });
    }
  }, [customLayout, id, dispatch]);

  const {
    isSelectingProducts,
    selectedProducts,
    startProductSelection,
    abortProductSelection,
    selectProduct,
    deselectProduct,
  } = useCompareProductsSelection();

  const abortProductSelectionFromCompareButton = useCallback(
    () => abortProductSelection('compareButton'),
    [abortProductSelection]
  );
  const abortProductSelectionFromBasket = useCallback(
    () => abortProductSelection('basket'),
    [abortProductSelection]
  );

  const selectProductById = useCallback<SelectProductToCompareById>(
    (productId, source) => {
      const selectedProduct = products.find(p => p.id === productId);
      if (selectedProduct) {
        selectProduct(source, selectedProduct);
      }
    },
    [products, selectProduct]
  );

  const deselectProductById = useCallback<DeselectProductToCompareById>(
    (productId, source) => {
      const deselectedProduct = products.find(p => p.id === productId);
      if (deselectedProduct) {
        deselectProduct(source, deselectedProduct);
      }
    },
    [products, deselectProduct]
  );

  const availableSortOptions = [...availableSorts]
    .concat('property.in_stock')
    .concat(
      propertyColumns.map(column => column.id).map(getSortKeyForProperty)
    );

  const availableSortOptionsTranslationTable = propertyColumns.reduce(
    (prev, item) => {
      // eslint-disable-next-line no-param-reassign
      prev[item.id] = getPropertyColumnName(item, t);
      return prev;
    },
    {}
  );

  /**
   * Select a suitable category to use as the "comparison category", ie. the context within which
   * you're comparing products.
   */
  const { categories, categoryPath } = productCollection;
  const comparisonCategory =
    categories?.length === 1
      ? // If the product collection represents exactly one category, use that
        categories[0]
      : // Otherwise, use the nearest parent category as determined by the category path
        categoryPath[categoryPath.length - 1];

  const showTitle = !(isMobile || isTablet);

  const compareProductsToggle = useMemo(
    () => (
      <S.CompareProductsToggle
        active={isSelectingProducts}
        activate={startProductSelection}
        deactivate={abortProductSelectionFromCompareButton}
        showTitle={showTitle}
      />
    ),
    [
      isSelectingProducts,
      startProductSelection,
      abortProductSelectionFromCompareButton,
      showTitle,
    ]
  );

  const productComparisonProps: ProductComparisonProps = {
    isSelectingProducts,
    selectedProducts,
    selectProductById,
    deselectProductById,
  };

  const supportedViewModes = [
    LayoutMode.GRID,
    LayoutMode.LIST,
    ...(isMobile ? [LayoutMode.COMPACT_LIST] : [LayoutMode.TABLE]),
  ];

  const hasExpertContent =
    expert && productContainerProps.pageInfo?.offset === 0;

  const tableView = isMobile ? (
    <CompactListLayout
      onClick={trackProductCollectionProductClick}
      products={products}
      {...productComparisonProps}
    />
  ) : (
    <TableLayout
      propertyColumns={propertyColumns}
      {...{
        products,
        currentSort,
        sortDirection,
      }}
      {...productComparisonProps}
    />
  );

  const activeLayout = customLayout || getLayout(preferredViewMode);

  /**
   * Tracks the layout change
   * Sets the new layout
   * @param newLayout LayoutMode
   */
  const changeLayout = (newLayout: LayoutMode) => {
    const convertLayoutToNonMobile = (layout: LayoutMode) =>
      layout === LayoutMode.COMPACT_LIST ? LayoutMode.TABLE : layout;
    trackProductCollectionLayoutChangeClick(
      convertLayoutToNonMobile(activeLayout),
      convertLayoutToNonMobile(newLayout)
    );

    setProductCollectionLayout(newLayout);
  };

  return (
    <S.ProductsWrapper>
      <ProductOfferContainer
        {...{ ...productContainerProps, id, currentSort }}
        layout={activeLayout}
        onLayoutChange={changeLayout}
        compareProductsToggle={compareProductsToggle}
        availableSorts={availableSortOptions}
        availableSortsTranslations={availableSortOptionsTranslationTable}
        supportedViewModes={supportedViewModes}
      >
        {activeLayout === LayoutMode.GRID && (
          <GridLayout
            expert={hasExpertContent ? expert : null}
            onClick={trackProductCollectionProductClick}
            products={products}
            sponsoredProducts={filteredSponsoredProducts}
            {...productComparisonProps}
          />
        )}

        {(activeLayout === LayoutMode.TABLE ||
          activeLayout === LayoutMode.COMPACT_LIST) &&
          tableView}

        {activeLayout === LayoutMode.LIST && (
          <ListLayout
            onClick={trackProductCollectionProductClick}
            products={products}
            {...productComparisonProps}
          />
        )}
      </ProductOfferContainer>

      <aside data-testid="bottom-ads">
        <AdContainer
          placement="panorama_footer"
          format="panorama_4_wde"
          min={861}
          max={10000}
        />

        <AdContainer
          placement="dubbelpanorama3"
          format="panorama_3"
          min={0}
          max={860}
        />
      </aside>

      {isSelectingProducts && (
        <CompareProductsBasket
          products={selectedProducts}
          compareUrl={
            selectedProducts.length > 1
              ? linkTo('productCompare', {
                  category: comparisonCategory,
                  products: selectedProducts,
                })
              : undefined
          }
          onEmpty={abortProductSelectionFromBasket}
          onProductDelete={deselectProductById}
        />
      )}
    </S.ProductsWrapper>
  );
};

const dispatchActions = {
  setProductCollectionLayout: setProductCollectionLayoutAction,
};

const mapStateToProps = (state: AppState) => ({
  customLayout: selectProductLayout(state),
  sponsoredProducts: selectSponsoredProducts(state),
});

export default connect(mapStateToProps, dispatchActions)(Products);
